import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useApiClient, buildRestaurantPath } from '../lib/api';
import { useConfig } from '../providers/config-provider';
import { useAuth } from '../providers/auth-provider';
import StatusPill from '../components/common/StatusPill';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { CALLS_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';

const STATUS_LABEL = {
  Open: 'Aberta',
  Claimed: 'Em atendimento',
  Resolved: 'Finalizada',
  Canceled: 'Cancelada',
  Expired: 'Expirada'
};

const STATUS_OPTIONS = [
  { value: 'Open', label: 'Abertas' },
  { value: 'Claimed', label: 'Em atendimento' },
  { value: 'Resolved', label: 'Finalizadas' },
  { value: 'All', label: 'Todas' }
];

const callsPageSize = 20;

const normalizeBase = (value) => (value || '').replace(/\/+$/, '');

const formatElapsed = (dateValue, referenceMs = Date.now()) => {
  if (!dateValue) {
    return '-';
  }
  const openedAt = new Date(dateValue).getTime();
  const diffSeconds = Math.max(0, Math.floor((referenceMs - openedAt) / 1000));

  if (diffSeconds < 60) {
    return `${diffSeconds}s`;
  }

  const minutes = Math.floor(diffSeconds / 60);
  if (minutes < 60) {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}min`;
};

const extractApiErrorMessage = (err, fallback) => {
  const payload = err?.response?.data;
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  return fallback;
};

const getOperationalPriority = (call) => {
  if (call.status === 'Open' && call.escalatedAt) return 0;
  if (call.status === 'Open') return 1;
  if (call.status === 'Claimed') return 2;
  return 3;
};

const sortCallsOperationally = (calls) => {
  return [...calls].sort((a, b) => {
    const priorityDiff = getOperationalPriority(a) - getOperationalPriority(b);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    if (a.status === 'Open' && b.status === 'Open') {
      return new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime();
    }

    if (a.status === 'Claimed' && b.status === 'Claimed') {
      return new Date(a.claimedAt || a.openedAt).getTime() - new Date(b.claimedAt || b.openedAt).getTime();
    }

    return new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime();
  });
};

const CallsPage = () => {
  const api = useApiClient();
  const { apiBase, restaurantId } = useConfig();
  const { user } = useAuth();

  const [allCalls, setAllCalls] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [claimInFlight, setClaimInFlight] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Open');
  const [isRestaurantOpen, setIsRestaurantOpen] = useState(true);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [nowTick, setNowTick] = useState(Date.now());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(CALLS_HELP_SECTIONS[0].key);

  const connectionRef = useRef(null);
  const pendingConnectionRef = useRef(null);
  const connectionCycleRef = useRef(0);
  const loadMoreRef = useRef(null);
  const knownCallIdsRef = useRef(new Set());
  const audioContextRef = useRef(null);
  const audioLastPlayedAtRef = useRef(0);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const summary = useMemo(() => {
    const openCount = allCalls.filter((item) => item.status === 'Open').length;
    const claimedCount = allCalls.filter((item) => item.status === 'Claimed').length;
    const resolvedCount = allCalls.filter((item) => item.status === 'Resolved').length;
    const escalatedCount = allCalls.filter((item) => item.status === 'Open' && item.escalatedAt).length;
    const oldestOpen = allCalls
      .filter((item) => item.status === 'Open')
      .sort((a, b) => new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime())[0];

    return {
      total: allCalls.length,
      openCount,
      claimedCount,
      resolvedCount,
      escalatedCount,
      oldestOpenElapsed: oldestOpen ? formatElapsed(oldestOpen.openedAt, nowTick) : '-'
    };
  }, [allCalls, nowTick]);

  const canReopen = user && ['AdminMaster', 'Admin', 'Manager'].includes(user.role);
  const isWaiter = user?.role === 'Waiter';

  const waiterActiveClaim = useMemo(() => {
    if (!isWaiter || !user?.id) {
      return null;
    }

    const ownedClaimed = allCalls.filter((item) => item.status === 'Claimed' && item.claimedByUserId === user.id);
    if (!ownedClaimed.length) {
      return null;
    }

    return [...ownedClaimed].sort((a, b) => new Date(a.claimedAt || a.openedAt).getTime() - new Date(b.claimedAt || b.openedAt).getTime())[0];
  }, [allCalls, isWaiter, user?.id]);

  const isWaiterLocked = Boolean(waiterActiveClaim);

  useEffect(() => {
    if (isWaiterLocked && selectedStatus !== 'Claimed') {
      setSelectedStatus('Claimed');
    }
  }, [isWaiterLocked, selectedStatus]);

  const visibleCalls = useMemo(() => {
    if (isWaiterLocked && waiterActiveClaim) {
      return [waiterActiveClaim];
    }

    if (selectedStatus === 'All') {
      return allCalls;
    }

    return allCalls.filter((item) => item.status === selectedStatus);
  }, [allCalls, isWaiterLocked, selectedStatus, waiterActiveClaim]);

  const playAlertSound = useCallback(() => {
    if (!soundEnabled) {
      return;
    }

    const now = Date.now();
    if (now - audioLastPlayedAtRef.current < 1200) {
      return;
    }

    audioLastPlayedAtRef.current = now;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }

    const context = audioContextRef.current;
    if (context.state === 'suspended') {
      context.resume().catch(() => undefined);
    }

    const startAt = context.currentTime;

    const sequence = [
      { frequency: 880, duration: 0.12, delay: 0 },
      { frequency: 1320, duration: 0.14, delay: 0.16 }
    ];

    sequence.forEach((tone) => {
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      oscillator.type = 'triangle';
      oscillator.frequency.value = tone.frequency;
      gainNode.gain.setValueAtTime(0.001, startAt + tone.delay);
      gainNode.gain.exponentialRampToValueAtTime(0.22, startAt + tone.delay + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startAt + tone.delay + tone.duration);

      oscillator.connect(gainNode);
      gainNode.connect(context.destination);

      oscillator.start(startAt + tone.delay);
      oscillator.stop(startAt + tone.delay + tone.duration + 0.02);
    });
  }, [soundEnabled]);

  const checkRestaurantStatus = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const response = await api.get(buildRestaurantPath(restaurantId, 'status'));
      if (response.data && typeof response.data.isOpen === 'boolean') {
        setIsRestaurantOpen(response.data.isOpen);
      }
    } catch (err) {
      console.error('Failed to check restaurant status', err);
    }
  }, [restaurantId, api]);

  const handleRealtime = useCallback((call) => {
    if (!call || call.restaurantId !== restaurantId) {
      return;
    }

    if (call.status === 'Open' && !knownCallIdsRef.current.has(call.id)) {
      knownCallIdsRef.current.add(call.id);
      playAlertSound();
    }

    if (call.lastRecalledAt) {
      playAlertSound();
    }

    // Waiters should not see calls claimed by others in their list
    if (userRef.current?.role === 'Waiter' && call.status === 'Claimed' && call.claimedByUserId !== userRef.current.id) {
      setAllCalls((current) => {
        const idx = current.findIndex((c) => c.id === call.id);
        if (idx >= 0) {
          const clone = [...current];
          clone.splice(idx, 1);
          return clone;
        }
        return current;
      });
      return;
    }

    setAllCalls((current) => {
      const idx = current.findIndex((c) => c.id === call.id);
      if (idx >= 0) {
        const clone = [...current];
        clone[idx] = call;
        return sortCallsOperationally(clone);
      }
      return sortCallsOperationally([call, ...current]);
    });
  }, [restaurantId, playAlertSound]);

  const disconnectSignalR = useCallback(async () => {
    connectionCycleRef.current += 1;

    const pending = pendingConnectionRef.current;
    pendingConnectionRef.current = null;

    const current = connectionRef.current;
    connectionRef.current = null;

    try {
      if (
        current &&
        restaurantId &&
        current.state === signalR.HubConnectionState.Connected
      ) {
        await current.invoke('LeaveRestaurantGroup', restaurantId);
      }
    } catch {
      // Ignore best-effort disconnect cleanup.
    }

    await Promise.all([
      pending ? pending.stop().catch(() => undefined) : Promise.resolve(),
      current ? current.stop().catch(() => undefined) : Promise.resolve(),
    ]);

    setConnectionState('disconnected');
  }, [restaurantId]);

  const connectSignalR = useCallback(async () => {
    if (!restaurantId || !apiBase) {
      return;
    }

    if (connectionRef.current || pendingConnectionRef.current) {
      return;
    }

    const cycle = connectionCycleRef.current + 1;
    connectionCycleRef.current = cycle;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${normalizeBase(apiBase)}/hubs/calls`, {
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
      .configureLogging(signalR.LogLevel.Information)
      .build();
    pendingConnectionRef.current = connection;

    connection.on('call.created', handleRealtime);
    connection.on('call.claimed', handleRealtime);
    connection.on('call.resolved', handleRealtime);
    connection.on('call.recalled', handleRealtime);
    connection.on('call.escalated', handleRealtime);
    connection.on('call.reopened', handleRealtime);

    connection.onreconnecting(() => {
      if (cycle !== connectionCycleRef.current) {
        return;
      }
      setConnectionState('reconnecting');
    });

    connection.onreconnected(() => {
      if (cycle !== connectionCycleRef.current) {
        return;
      }
      setConnectionState('connected');
      connection.invoke('JoinRestaurantGroup', restaurantId).catch(() => undefined);
    });

    connection.onclose(() => {
      if (cycle !== connectionCycleRef.current) {
        return;
      }
      if (pendingConnectionRef.current === connection) {
        pendingConnectionRef.current = null;
      }
      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }
      setConnectionState('disconnected');
    });

    try {
      await connection.start();
      if (cycle !== connectionCycleRef.current) {
        await connection.stop().catch(() => undefined);
        return;
      }

      await connection.invoke('JoinRestaurantGroup', restaurantId);
      if (cycle !== connectionCycleRef.current) {
        await connection.stop().catch(() => undefined);
        return;
      }

      pendingConnectionRef.current = null;
      connectionRef.current = connection;
      setConnectionState('connected');
      setError(null);
    } catch (err) {
      if (pendingConnectionRef.current === connection) {
        pendingConnectionRef.current = null;
      }
      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }
      if (cycle !== connectionCycleRef.current) {
        await connection.stop().catch(() => undefined);
        return;
      }
      console.error(err);
      setError('Não foi possível conectar em tempo real.');
      setConnectionState('disconnected');
      await connection.stop().catch(() => undefined);
    }
  }, [restaurantId, apiBase, handleRealtime]);

  const loadCalls = useCallback(async ({ reset = false, pageToLoad = 1 } = {}) => {
    if (!restaurantId) {
      return;
    }

    const targetPage = reset ? 1 : pageToLoad;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await api.get(buildRestaurantPath(restaurantId, 'calls'), {
        params: {
          page: targetPage,
          pageSize: callsPageSize
        }
      });

      const payload = response.data;
      const items = payload?.items || payload || [];
      const total = payload?.totalItems ?? items.length;

      setTotalItems(total);
      if (reset) {
        knownCallIdsRef.current = new Set(items.map((item) => item.id));
      } else {
        items.forEach((item) => knownCallIdsRef.current.add(item.id));
      }

      setAllCalls((current) => {
        const base = reset ? [] : current;
        const mergedById = new Map(base.map((call) => [call.id, call]));

        items.forEach((item) => {
          mergedById.set(item.id, item);
        });

        const merged = sortCallsOperationally(Array.from(mergedById.values()));

        setHasMore(merged.length < total && items.length === callsPageSize);
        return merged;
      });

      setError(null);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Não foi possível carregar as chamadas.');
      setHasMore(false);
    } finally {
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [restaurantId, api]);

  useEffect(() => {
    document.title = 'Chamadas | Orion WaiterCall';
  }, []);

  useEffect(() => {
    const timerId = setInterval(() => {
      setNowTick(Date.now());
    }, 15000);

    return () => {
      clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    connectSignalR();
    return () => {
      disconnectSignalR().catch(() => undefined);
    };
  }, [connectSignalR, disconnectSignalR]);

  useEffect(() => {
    if (restaurantId) {
      setPage(1);
      loadCalls({ reset: true });
      checkRestaurantStatus();

      const intervalId = setInterval(checkRestaurantStatus, 60000); // Check every minute
      return () => clearInterval(intervalId);
    }
  }, [restaurantId, loadCalls, checkRestaurantStatus]);

  useEffect(() => {
    if (page <= 1) {
      return;
    }

    loadCalls({ reset: false, pageToLoad: page });
  }, [page, loadCalls]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting) {
        setPage((current) => current + 1);
      }
    }, { root: null, rootMargin: '240px 0px', threshold: 0.1 });

    const target = loadMoreRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);


  const claim = async (call) => {
    if (!restaurantId || claimInFlight) {
      return;
    }

    if (isWaiterLocked && waiterActiveClaim && waiterActiveClaim.id !== call.id) {
      setSnackbar('Finalize seu atendimento atual antes de assumir uma nova mesa.');
      setTimeout(() => setSnackbar(null), 3200);
      return;
    }
    setClaimInFlight(true);
    setActionLoadingId(call.id);
    try {
      await api.post(buildRestaurantPath(restaurantId, `calls/${call.id}/claim`));
      setSnackbar('Chamada assumida.');
      setTimeout(() => setSnackbar(null), 2600);
      await loadCalls();
    } catch (err) {
      console.error(err);
      setSnackbar(extractApiErrorMessage(err, 'Não foi possível assumir.'));
      setTimeout(() => setSnackbar(null), 3200);
      await loadCalls();
    } finally {
      setActionLoadingId(null);
      setClaimInFlight(false);
    }
  };

  const resolve = async (call) => {
    if (!restaurantId) {
      return;
    }
    setActionLoadingId(call.id);
    try {
      await api.post(buildRestaurantPath(restaurantId, `calls/${call.id}/resolve`));
      setSnackbar('Chamada finalizada.');
      setTimeout(() => setSnackbar(null), 2600);
      handleRealtime({ ...call, status: 'Resolved', resolvedAt: new Date().toISOString() });
    } catch (err) {
      console.error(err);
      setSnackbar(err?.response?.data || 'Não foi possível finalizar.');
      setTimeout(() => setSnackbar(null), 3200);
    } finally {
      setActionLoadingId(null);
    }
  };

  const reopen = async (call) => {
    if (!restaurantId || !canReopen) {
      return;
    }

    setActionLoadingId(call.id);
    try {
      await api.post(buildRestaurantPath(restaurantId, `calls/${call.id}/reopen`));
      setSnackbar('Atendimento reaberto para a fila.');
      setTimeout(() => setSnackbar(null), 2600);
      await loadCalls({ reset: true });
    } catch (err) {
      console.error(err);
      setSnackbar(extractApiErrorMessage(err, 'Não foi possível reabrir atendimento.'));
      setTimeout(() => setSnackbar(null), 3200);
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <section className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Central de Chamadas</h2>
            <p className="mt-1 text-sm text-slate-600">
              Operação em tempo real com priorização de atendimento por mesa.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
              connectionState === 'connected'
                ? 'bg-emerald-100 text-emerald-700'
                : connectionState === 'reconnecting'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-rose-100 text-rose-700'
            }`}>
              {connectionState === 'connected'
                ? 'Tempo real conectado'
                : connectionState === 'reconnecting'
                ? 'Reconectando tempo real'
                : 'Tempo real desconectado'}
            </span>

            <button
              type="button"
              onClick={() => setSoundEnabled((prev) => !prev)}
              className={`rounded-lg px-3 py-2 text-xs font-semibold ${
                soundEnabled
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              {soundEnabled ? 'Som: ligado' : 'Som: desligado'}
            </button>

            <ContextHelpButton
              label="Ajuda sobre chamadas"
              onClick={() => {
                setHelpSectionKey('operation');
                setIsHelpOpen(true);
              }}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-6">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total no painel</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{summary.total}</p>
          </div>

          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">Abertas</p>
            <p className="mt-1 text-2xl font-bold text-rose-700">{summary.openCount}</p>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Em atendimento</p>
            <p className="mt-1 text-2xl font-bold text-amber-700">{summary.claimedCount}</p>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Finalizadas</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{summary.resolvedCount}</p>
          </div>

          <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-600">Escalonadas</p>
            <p className="mt-1 text-2xl font-bold text-fuchsia-700">{summary.escalatedCount}</p>
          </div>

          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">A mais antiga aberta</p>
            <p className="mt-1 text-2xl font-bold text-indigo-700">{summary.oldestOpenElapsed}</p>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (isWaiterLocked) {
                    return;
                  }
                  setSelectedStatus(option.value);
                }}
                disabled={isWaiterLocked}
                className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  selectedStatus === option.value
                    ? 'bg-indigo-600 text-white shadow'
                    : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              setPage(1);
              loadCalls({ reset: true });
            }}
            className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 sm:ml-auto sm:w-auto"
          >
            Atualizar
          </button>
        </div>
      </div>

      {isWaiterLocked && waiterActiveClaim ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Atendimento em andamento</p>
              <h3 className="mt-1 text-lg font-bold text-amber-900">
                Mesa {waiterActiveClaim.tableNumberLabel || 'N/D'} {waiterActiveClaim.tableArea ? `(${waiterActiveClaim.tableArea})` : ''}
              </h3>
              <p className="mt-1 text-sm text-amber-800">
                Tela bloqueada para novas assunções até finalizar esta chamada.
              </p>
            </div>
            <button
              type="button"
              onClick={() => resolve(waiterActiveClaim)}
              disabled={actionLoadingId === waiterActiveClaim.id}
              className="rounded-lg border border-amber-500 bg-white px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-70"
            >
              Finalizar atendimento
            </button>
          </div>
        </div>
      ) : null}

      {!isRestaurantOpen && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
          O restaurante está fechado no momento. Novas chamadas não serão geradas.
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm font-semibold text-slate-600 shadow-sm">
          Carregando chamadas...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>
      ) : visibleCalls.length ? (
        <div className="grid gap-3">
          {visibleCalls.map((call) => (
            <article
              key={call.id}
              className={`grid gap-3 rounded-2xl border bg-white p-4 shadow-sm md:grid-cols-[1fr,auto] ${
                call.status === 'Open'
                  ? 'border-rose-200'
                  : call.status === 'Claimed'
                  ? 'border-amber-200'
                  : 'border-slate-200'
              }`}
            >
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <StatusPill
                    label={STATUS_LABEL[call.status] || call.status}
                    variant={
                      call.status === 'Open'
                        ? 'info'
                        : call.status === 'Claimed'
                        ? 'warning'
                        : call.status === 'Resolved'
                        ? 'success'
                        : 'muted'
                    }
                  />
                  <h3 className="text-lg font-semibold text-slate-900">
                    Mesa {call.tableNumberLabel || 'N/D'} {call.tableArea ? `(${call.tableArea})` : ''}
                  </h3>
                  <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                    Há {formatElapsed(call.openedAt, nowTick)}
                  </span>
                  {call.recallCount > 0 ? (
                    <span className="rounded-md bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700">
                      Rechamada x{call.recallCount}
                    </span>
                  ) : null}
                  {call.status === 'Open' && call.escalatedAt ? (
                    <span className="rounded-md bg-fuchsia-100 px-2 py-1 text-xs font-semibold text-fuchsia-700">
                      Escalonada
                    </span>
                  ) : null}
                </div>
                <dl className="grid grid-cols-2 gap-2 text-sm text-slate-700 sm:grid-cols-4">
                  <div>
                    <dt className="text-xs uppercase text-slate-500">Abertura</dt>
                      <dd className="font-semibold">{new Date(call.openedAt).toLocaleString()}</dd>
                  </div>
                  {call.claimedAt ? (
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Assumida</dt>
                      <dd className="font-semibold">{new Date(call.claimedAt).toLocaleTimeString()}</dd>
                    </div>
                  ) : null}
                  {(call.status === 'Claimed' || call.status === 'Resolved') && call.claimedByUserName ? (
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Atendente</dt>
                      <dd className="font-semibold truncate" title={call.claimedByUserName}>
                        {call.claimedByUserName}
                      </dd>
                    </div>
                  ) : null}
                  {call.resolvedAt ? (
                    <div>
                      <dt className="text-xs uppercase text-slate-500">Finalizada</dt>
                      <dd className="font-semibold">{new Date(call.resolvedAt).toLocaleTimeString()}</dd>
                    </div>
                  ) : null}
                  {call.claimedSlaDeadlineAt && call.status === 'Claimed' ? (
                    <div>
                      <dt className="text-xs uppercase text-slate-500">SLA</dt>
                      <dd className="font-semibold">{new Date(call.claimedSlaDeadlineAt).toLocaleTimeString()}</dd>
                    </div>
                  ) : null}
                </dl>
              </div>
              <div className="flex flex-col gap-2 md:items-end md:justify-center">
                {call.status === 'Open' ? (
                  <button
                    type="button"
                    onClick={() => claim(call)}
                    disabled={claimInFlight || actionLoadingId === call.id || (isWaiterLocked && waiterActiveClaim?.id !== call.id)}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-70"
                  >
                    Assumir chamada
                  </button>
                ) : null}
                {call.status === 'Claimed' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => resolve(call)}
                      disabled={actionLoadingId === call.id || (user?.role === 'Waiter' && call.claimedByUserId !== user.id)}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-70"
                      title={user?.role === 'Waiter' && call.claimedByUserId !== user.id ? 'Você só pode finalizar chamadas assumidas por você.' : ''}
                    >
                      Finalizar atendimento
                    </button>
                    {canReopen ? (
                      <button
                        type="button"
                        onClick={() => reopen(call)}
                        disabled={actionLoadingId === call.id}
                        className="rounded-lg border border-fuchsia-300 bg-fuchsia-50 px-4 py-2 text-sm font-semibold text-fuchsia-700 hover:bg-fuchsia-100 disabled:opacity-70"
                      >
                        Reabrir para fila
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </article>
          ))}

          <div ref={loadMoreRef} className="py-2 text-center text-sm text-slate-500">
            {loadingMore ? 'Carregando mais chamadas...' : hasMore ? 'Role para carregar mais' : `Fim da lista (${totalItems})`}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-600">
          Nenhuma chamada encontrada.
        </div>
      )}

      {snackbar ? <div className="fixed right-6 bottom-6 z-50 rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg">{snackbar}</div> : null}

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Central de Chamadas"
        description="Revise regras de operação, prioridade, tempo real e ações disponíveis no atendimento."
        sections={CALLS_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

export default CallsPage;
